import { createContext, useCallback, useContext, useMemo, useState } from "react";

// Safe default so HMR remounts never read null (same pattern as ExploreContext).
const SAFE_OVERLAY = { openId: null, openSource: null, openListing: (_id, _source) => {}, close: () => {} };
const DetailOverlayContext = createContext(SAFE_OVERLAY);

/**
 * Holds the place currently shown in the in-page detail overlay. Cards call
 * `openListing(id, source)` instead of navigating, so the site header, the page
 * behind it and the scroll position all stay put.
 *
 * Bookable partner experiences open the same way, as the same card with their
 * own body inside it. /experience/:id stays live for links already shared.
 */
export function DetailOverlayProvider({ children }) {
  const [open, setOpen] = useState(null);
  const openListing = useCallback((id, source = "local") => {
    if (!id) return setOpen(null);
    setOpen({ id, source });
  }, []);
  const close = useCallback(() => setOpen(null), []);
  const value = useMemo(
    () => ({ openId: open?.id || null, openSource: open?.source || null, openListing, close }),
    [open, openListing, close]
  );

  return <DetailOverlayContext.Provider value={value}>{children}</DetailOverlayContext.Provider>;
}

export const useDetailOverlay = () => useContext(DetailOverlayContext);